import { useEffect, useState } from "react";
import {
  AUTOMOTIVE_SCHEDULE_INTERVALS,
  intervalMinutesFromSource,
  schedulePayloadFromInterval,
} from "../../../../services/automotiveSourceControls";
import { formatCompletedTime } from "../../intelligence/intelligenceLabels";
import SourceHealthBadge from "../SourceHealthBadge";

const AutomotiveScheduleModal = ({
  source,
  readinessRecord,
  isOpen,
  isSaving,
  onClose,
  onSave,
}) => {
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    if (!source || !isOpen) return;
    setScheduleEnabled(Boolean(source.scheduleEnabled));
    const minutes = intervalMinutesFromSource(source);
    setIntervalMinutes(minutes == null ? "" : String(minutes));
    setConfirmed(false);
  }, [source, isOpen]);

  if (!isOpen || !source) return null;

  const compliance = readinessRecord?.compliance;
  const blockers = readinessRecord?.scheduleExecutionBlocked
    ? readinessRecord?.regressionWarnings?.map((w) => w.message) || []
    : [];
  const scheduleBlocked = compliance?.blocksSchedule;
  const needsConfirm = scheduleEnabled && !source.scheduleEnabled;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (needsConfirm && !confirmed) return;
    const minutes = intervalMinutes === "" ? null : Number(intervalMinutes);
    const payload = schedulePayloadFromInterval(minutes, scheduleEnabled && minutes != null);
    onSave(source, payload);
  };

  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="admin-re-modal panel-card"
        role="dialog"
        aria-labelledby="automotive-schedule-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Vehicle Market · Schedule</p>
            <h2 id="automotive-schedule-modal-title">Schedule: {source.sourceName}</h2>
            <p className="muted admin-re-source-code">{source.sourceCode || source.sourceKey}</p>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>

        <form className="admin-re-modal-body" onSubmit={handleSubmit}>
          {compliance ? (
            <div className="automotive-readiness-panel__sync muted">
              Compliance: {compliance.complianceStatusBadge || compliance.complianceStatus}
              {" · "}
              Schedule allowed: {compliance.scheduleAllowed}
              {compliance.scheduleLimited ? " (limited)" : ""}
            </div>
          ) : null}

          {scheduleBlocked || blockers.length ? (
            <div className="automotive-readiness-panel__regression automotive-readiness-panel__regression--warning">
              {blockers[0] ||
                compliance?.recommendedNextAction ||
                "Schedule may be blocked by compliance or engineering guardrails."}
            </div>
          ) : null}

          <label className="admin-re-modal-field admin-re-modal-toggle">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(event) => setScheduleEnabled(event.target.checked)}
              disabled={!source.supportsSchedule}
            />
            <span>Schedule enabled</span>
          </label>

          <label className="admin-re-modal-field">
            <span>Run interval</span>
            <select
              value={intervalMinutes}
              disabled={!scheduleEnabled || !source.supportsSchedule}
              onChange={(event) => setIntervalMinutes(event.target.value)}
            >
              {AUTOMOTIVE_SCHEDULE_INTERVALS.map((interval) => (
                <option
                  key={interval.label}
                  value={interval.minutes == null ? "" : String(interval.minutes)}
                >
                  {interval.label}
                </option>
              ))}
            </select>
          </label>

          <div className="admin-re-modal-meta muted">
            <div>Last run: {formatCompletedTime(source.lastRunAt)}</div>
            <div>Next run: {formatCompletedTime(source.nextRunAt)}</div>
            <div>
              Status: <SourceHealthBadge status={source.health || source.lastStatus || "unknown"} />
            </div>
          </div>

          {needsConfirm ? (
            <label className="admin-re-modal-field admin-re-modal-toggle">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              <span>
                I confirm enabling the schedule for this source (ETL worker runs ingest, not API server).
              </span>
            </label>
          ) : null}

          <footer className="admin-re-modal-footer">
            <button type="button" className="midpoint-share-button" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="admin-dashboard__action-btn"
              disabled={isSaving || (needsConfirm && !confirmed)}
            >
              {isSaving ? "Saving…" : "Save schedule"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default AutomotiveScheduleModal;
